
import React, { useState } from 'react';
import { useNotifications, type Notification } from '@/hooks/use-notifications';
import { formatDistance } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Check, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import confetti from 'canvas-confetti';

interface NotificationItemProps {
  notification: Notification;
  compact?: boolean;
}

export const NotificationItem = ({ notification, compact = false }: NotificationItemProps) => {
  const { markAsRead, deleteNotification } = useNotifications(notification.recipient_id);
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
  
  const isWelcomeNotification = notification.type === 'welcome';
  
  // Update welcome image path to use the new image
  const welcomeImagePath = isWelcomeNotification 
    ? (notification.metadata && typeof notification.metadata === 'object' && 'welcome_image' in notification.metadata 
        ? notification.metadata.welcome_image as string 
        : "/lovable-uploads/c950070c-60a0-4c18-a0f2-efe41a37b71a.png")
    : "";
  
  const triggerConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });
  };
  
  const handleNotificationClick = () => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    
    if (isWelcomeNotification) {
      setShowWelcomeDialog(true);
      setTimeout(triggerConfetti, 300);
    }
  };
  
  return (
    <>
      <div className={cn(
        "flex gap-3 items-start p-3 group hover:bg-muted/50 transition-colors cursor-pointer rounded-md border-t border-border",
        !notification.is_read && "bg-muted/20",
        compact && "p-2"
      )}>
        <div className="flex-shrink-0">
          <Avatar className={cn(compact ? "h-8 w-8" : "h-10 w-10")}>
            {isWelcomeNotification ? (
              <AvatarImage src={welcomeImagePath} alt="Ink Webtoons" />
            ) : (
              <AvatarImage src={notification.sender?.avatar_url ?? ""} alt={notification.sender?.username || "Usuário"} />
            )}
            <AvatarFallback>
              {isWelcomeNotification 
                ? "IW" 
                : (notification.sender?.username?.charAt(0)?.toUpperCase() || "U")}
            </AvatarFallback>
          </Avatar>
        </div>
        
        <div className="flex-1 min-w-0" onClick={handleNotificationClick}>
          <div className="flex flex-col gap-1">
            <p className={cn(
              "text-sm line-clamp-2", 
              !notification.is_read && "font-medium"
            )}>
              {notification.message}
            </p>
            <p className="text-xs text-muted-foreground">
              {formatDistance(new Date(notification.created_at), new Date(), { 
                addSuffix: true,
                locale: ptBR
              })}
            </p>
          </div>
        </div>
        
        <div className={cn(
          "flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity",
          compact && "flex-col"
        )}>
          {!notification.is_read && (
            <Button
              variant="outline"
              size="icon"
              className={compact ? "h-7 w-7" : ""}
              onClick={() => markAsRead(notification.id)}
              title="Marcar como lida"
            >
              <Check className={cn("h-4 w-4", compact && "h-3 w-3")} />
            </Button>
          )}
          
          <Button
            variant="outline"
            size="icon"
            className={compact ? "h-7 w-7" : ""}
            onClick={() => deleteNotification(notification.id)}
            title="Remover notificação"
          >
            <Trash2 className={cn("h-4 w-4", compact && "h-3 w-3")} />
          </Button>
        </div>
      </div>
      
      {isWelcomeNotification && (
        <Dialog open={showWelcomeDialog} onOpenChange={setShowWelcomeDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-center text-xl">Bem-vindo ao Ink Webtoons!</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center space-y-4 py-4">
              <img 
                src={welcomeImagePath}
                alt="Boas Vindas ao Ink Webtoons" 
                className="h-48 w-auto"
              />
              <p className="text-center">
                Estamos muito felizes em ter você conosco! Explore incríveis webtoons, 
                compartilhe suas criações e conecte-se com outros criativos.
              </p>
              <Button 
                onClick={() => setShowWelcomeDialog(false)}
                className="mt-4 bg-primary hover:bg-primary/90"
              >
                Começar a explorar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
